import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { BytesToSizePipe } from '../../../../shared/pipes/bytes-to-size-pipe';
import moment from 'moment';
import { MatRipple } from '@angular/material/core';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { Subject, takeUntil } from 'rxjs';
import { WebsiteGroupSnapshot } from '../../../../shared/models/usage.models';

@Component({
  selector: 'app-site-disk-usage',
  imports: [
    MatCardModule,
    FontAwesomeModule,
    MatProgressBarModule,
    MatTooltipModule,
    BytesToSizePipe,
    MatRipple,
  ],
  templateUrl: './site-disk-usage.component.html',
  styleUrl: './site-disk-usage.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteDiskUsageComponent implements OnInit, OnDestroy {
  private websocketService = inject(WebsocketService);

  websiteData = input.required<WebsiteData>();

  diskUsage = computed(() => this.websiteData().quotaAndWebsiteDiskUsage);

  siteTotal = computed(() => {
    const data = this.diskUsage()?.websiteDiskUsageSnapshot?.websiteUsage;
    const newUsages = this.newUsages();
    const filesSize = newUsages.files ? newUsages.files : data ? data.totalFilesSize : 0;
    const mailboxesSize = newUsages.mailboxes
      ? newUsages.mailboxes
      : data
        ? data.totalMailboxesSize
        : 0;
    const databasesSize = newUsages.databases
      ? newUsages.databases
      : data
        ? data.totalDatabasesSize
        : 0;
    return filesSize + mailboxesSize + databasesSize;
  });

  homeDirPercentage = computed(() => {
    if (!this.diskUsage()) return 0;
    const { blockHard, blockUsed } = this.diskUsage()!.quotaSnapshot;
    return (blockUsed / blockHard) * 100;
  });

  siteTotalPercentage = computed(() => {
    const total = this.diskUsage()?.quotaSnapshot?.blockHard;
    if (!total) return 0;
    return (this.siteTotal() / total) * 100;
  });

  lastUpdated = computed(() => {
    const date = this.diskUsage()?.quotaSnapshot.date;
    return date ? moment(date * 1000).format('DD.MM.YYYY, hh:mm:ss') : 'N/A';
  });

  updateInterval = computed(() => {
    const interval = this.diskUsage()?.quotaSnapshot.interval;
    return interval ? interval / 60 : 60;
  });

  syncDowntime = computed(() => this.diskUsage()?.websiteDiskUsageSnapshot?.interval); // Seconds

  newUsages = signal<{ files?: number; mailboxes?: number; databases?: number }>({});

  filesUsage = computed(() =>
    this.newUsages().files
      ? this.newUsages().files
      : this.diskUsage()?.websiteDiskUsageSnapshot?.websiteUsage?.totalFilesSize,
  );

  mailboxesUsage = computed(() =>
    this.newUsages().mailboxes
      ? this.newUsages().mailboxes
      : this.diskUsage()?.websiteDiskUsageSnapshot?.websiteUsage?.totalMailboxesSize,
  );

  databasesUsage = computed(() =>
    this.newUsages().databases
      ? this.newUsages().databases
      : this.diskUsage()?.websiteDiskUsageSnapshot?.websiteUsage?.totalDatabasesSize,
  );

  syncUsage = output();

  isDisabled = signal(false);

  destroyed$ = new Subject();

  ngOnInit(): void {
    const snapshot = this.diskUsage()?.websiteDiskUsageSnapshot;
    if (snapshot) {
      const remainingDowntime = (snapshot.date + snapshot.interval) * 1000 - Date.now();
      if (remainingDowntime > 0) {
        this.isDisabled.set(true);
        setTimeout(() => {
          this.isDisabled.set(false);
        }, remainingDowntime);
      }
    }

    this.websocketService
      .listen<{ payload: WebsiteGroupSnapshot }>('websiteGroupUsageUpdated')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data) => {
        const websiteUsage = data.payload.websiteUsage.find(
          (w) => w.websiteId === this.websiteData().website.id,
        );
        const newFilesUsage = websiteUsage?.size;
        const newMailboxUsage = websiteUsage?.mailboxUsage.reduce(
          (acc, curr) => acc + curr.blockUsed,
          0,
        );
        const newDatabaseUsage = websiteUsage?.databasesUsage.reduce(
          (acc, curr) => acc + curr.size,
          0,
        );
        this.newUsages.set({
          files: newFilesUsage,
          mailboxes: newMailboxUsage,
          databases: newDatabaseUsage,
        });
      });
  }

  syncDiskUsage(): void {
    this.isDisabled.set(true);
    this.syncUsage.emit();

    const downtime = this.syncDowntime();
    if (downtime)
      setTimeout(() => {
        this.isDisabled.set(false);
      }, downtime * 1000);
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
